import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { compressImageFile } from '../utils/compressImage';
import { trackEvent } from '../utils/analytics';
import { colors, radii } from '../theme/tokens';

const MAX_IMAGES = 5;
const MAX_FILE_SIZE_MB = 8;

type UploadedImage = {
  id: string;
  name: string;
  previewUrl: string;
  type: string;
  data: string;
};

const serviceTypes = [
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'kitchen-renovation', label: 'Kitchen renovation' },
  { value: 'bathroom-renovation', label: 'Bathroom renovation' },
  { value: 'house-renovation', label: 'House renovation / refurbishment' },
  { value: 'house-extension', label: 'House extension' },
  { value: 'loft-conversion', label: 'Loft conversion' },
  { value: 'maintenance', label: 'Property maintenance / repairs' },
  { value: 'other', label: 'Something else' },
];

const emptyForm = { name: '', email: '', phone: '', serviceType: '', message: '' };

const QuoteForm = () => {
  const [form, setForm] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imagesRef = useRef<UploadedImage[]>([]);

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(() => () => imagesRef.current.forEach((img) => URL.revokeObjectURL(img.previewUrl)), []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      setError(`You can attach up to ${MAX_IMAGES} photos.`);
      e.target.value = '';
      return;
    }
    if (selected.length > remaining) setError(`Only ${MAX_IMAGES} photos can be attached — extra files were skipped.`);

    setIsProcessingImages(true);
    try {
      const next: UploadedImage[] = [];
      for (const file of selected.slice(0, remaining)) {
        if (!file.type.startsWith('image/')) {
          setError(`"${file.name}" isn't an image file.`);
          continue;
        }
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          setError(`"${file.name}" is over ${MAX_FILE_SIZE_MB}MB. Please use a smaller photo.`);
          continue;
        }
        const compressed = await compressImageFile(file);
        next.push({
          id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
          name: compressed.name,
          previewUrl: URL.createObjectURL(file),
          type: compressed.type,
          data: compressed.data,
        });
      }
      if (next.length) setImages((prev) => [...prev, ...next]);
    } catch {
      setError("Sorry, we couldn't process one of the photos. Please try again.");
    } finally {
      setIsProcessingImages(false);
      e.target.value = '';
    }
  };

  const removeImage = (id: string) =>
    setImages((prev) => {
      const target = prev.find((img) => img.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((img) => img.id !== id);
    });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/quote-request.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          serviceCategory: 'Service Request',
          images: images.map(({ name, type, data }) => ({ name, type, data })),
        }),
      });
      if (!response.ok) throw new Error('Failed to submit form');
      trackEvent('generate_lead', { form: 'quote', service_type: form.serviceType, photos: images.length });
      images.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      setImages([]);
      setForm(emptyForm);
      setSubmitted(true);
    } catch {
      setError("Sorry, something went wrong sending your request. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const cardSx = {
    p: { xs: 3, md: 4 },
    borderRadius: radii.xl,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
  };

  if (submitted) {
    return (
      <Box sx={{ ...cardSx, textAlign: 'center', py: { xs: 6, md: 8 } }} role="status" aria-live="polite">
        <CheckCircleOutlineIcon sx={{ fontSize: 56, color: colors.white, mb: 2 }} />
        <Typography component="h3" variant="h4" sx={{ color: colors.text, mb: 1.5 }}>
          Thanks — your request is on its way
        </Typography>
        <Typography sx={{ color: colors.textMuted, maxWidth: 420, mx: 'auto', mb: 4 }}>
          We&apos;ll review the details{images.length ? ' and photos' : ''} and get back to you within 24 hours.
        </Typography>
        <Button variant="outlined" onClick={() => setSubmitted(false)}>
          Send another request
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate={false} aria-label="Free quote request" sx={cardSx}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <TextField label="Full name" name="name" value={form.name} onChange={handleChange} required fullWidth autoComplete="name" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Phone" name="phone" type="tel" value={form.phone} onChange={handleChange} fullWidth autoComplete="tel" inputProps={{ inputMode: 'tel' }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Email" name="email" type="email" value={form.email} onChange={handleChange} required fullWidth autoComplete="email" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField select label="What do you need?" name="serviceType" value={form.serviceType} onChange={handleChange} required fullWidth>
            {serviceTypes.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Tell us about the job"
            name="message"
            value={form.message}
            onChange={handleChange}
            multiline
            minRows={4}
            fullWidth
            placeholder="Room sizes, what's currently there, access, timings — anything that helps us quote accurately."
          />
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: radii.md,
              border: `1px dashed ${colors.borderStrong}`,
              backgroundColor: 'rgba(255,255,255,0.02)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ fontWeight: 600, color: colors.text }}>Add photos (optional)</Typography>
                <Typography sx={{ color: colors.textSubtle, fontSize: '0.875rem' }}>
                  Up to {MAX_IMAGES} photos · helps us quote without a first visit
                </Typography>
              </Box>
              <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={handleImageSelect} />
              <Button
                type="button"
                variant="outlined"
                size="small"
                startIcon={isProcessingImages ? <CircularProgress size={16} sx={{ color: colors.white }} /> : <PhotoCameraOutlinedIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessingImages || images.length >= MAX_IMAGES || isSubmitting}
              >
                {isProcessingImages ? 'Processing…' : images.length ? 'Add more' : 'Choose photos'}
              </Button>
            </Box>

            {!!images.length && (
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 2, display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))' }}>
                {images.map((image) => (
                  <Box component="li" key={image.id} sx={{ position: 'relative', aspectRatio: '1 / 1', borderRadius: radii.sm, overflow: 'hidden', border: `1px solid ${colors.border}` }}>
                    <Box component="img" src={image.previewUrl} alt={`Attached photo ${image.name}`} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    <IconButton
                      aria-label={`Remove ${image.name}`}
                      onClick={() => removeImage(image.id)}
                      size="small"
                      sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(0,0,0,0.65)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.85)' } }}
                    >
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Button type="submit" variant="contained" size="large" fullWidth disabled={isSubmitting || isProcessingImages} endIcon={!isSubmitting && <ArrowForwardIcon />}>
            {isSubmitting ? <CircularProgress size={22} sx={{ color: colors.black }} /> : 'Request my free quote'}
          </Button>
          <Typography sx={{ color: colors.textSubtle, fontSize: '0.8rem', mt: 1.5, textAlign: 'center' }}>
            No obligation. We only use your details to respond to your enquiry.
          </Typography>
        </Grid>
      </Grid>

      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuoteForm;
